import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Actions} from '@ngneat/effects-ng';
import {GridDataResult} from '@progress/kendo-angular-grid';
import {ConfigService} from '@q9elements/ui-kit/common';
import {GridFetchParams, GridQuery, GridStore, selectEntities} from '@q9elements/ui-kit/components';
import {map} from 'rxjs';
import {finalize} from 'rxjs/operators';

import {extractNestedFields} from 'src/app/util/grid-context-menu.utils';
import {CustomViewConfig} from '../../../models/custom-view.interface';
import {CustomMetadataEntity} from './custom-metadata-entity';

@Injectable({providedIn: 'root'})
export class CustomMetadataQuery extends GridQuery<CustomMetadataEntity> {
  protected actions: Actions = inject(Actions);

  private fetchConfig: CustomViewConfig;

  constructor(store: GridStore<CustomMetadataEntity>, http: HttpClient) {
    super(store, http);
  }

  public setFetchConfig(config: CustomViewConfig) {
    this.fetchConfig = config;
  }

  private getFetchConfig(): CustomViewConfig {
    return this.fetchConfig;
  }

  override fetchEntities(params: GridFetchParams) {
    if (this.fetchConfig) {
      return this.handleFetchByConfig(params);
    }

    const state = this.store.getValue();
    const url = `${ConfigService.environment.API_ENDPOINT}/refmodel/${state.refModel}/custom-view/${state.viewId}`;

    const queryParams = {
      skip: params.skip,
      limit: params.take || 100
    };

    return super.fetchEntities<{list: GridDataResult[]; totalItems: number}>(queryParams, url).pipe(
      map(({list, totalItems}) => {
        return {
          entities: list.map(item => extractNestedFields(item, state.columns)),
          total: totalItems
        };
      }),
      finalize(() => {
        if (state.reselectActiveEntity) {
          this.actions.dispatch(selectEntities({ids: state.active}));
          this.store.update(state => ({...state, reselectActiveEntity: false}));
        }
      })
    );
  }

  private handleFetchByConfig(params: GridFetchParams) {
    const state = this.store.getValue();
    const skip = params.skip;
    const limit = params.take || 100;
    const composePayloadFn = this.fetchConfig.composePayloadFn;
    const payload = composePayloadFn({
      skip,
      limit,
      filters: params.filters,
      sort: params.sort
    });

    return this.fetchConfig.fetchFn(payload).pipe(
      map(({list, totalItems}) => {
        return {
          entities: list.map(item => extractNestedFields(item, state.columns)),
          total: totalItems
        };
      }),
      finalize(() => {
        if (state.reselectActiveEntity) {
          this.actions.dispatch(selectEntities({ids: state.active}));
          this.store.update(state => ({...state, reselectActiveEntity: false}));
        }
      })
    );
  }
}

export default CustomMetadataQuery;
